import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import { Badge, Button, FlatTreeItem, makeStyles, mergeClasses, Spinner, tokens, Tooltip, useRestoreFocusTarget } from '@fluentui/react-components'
import {
    bundleIcon,
    CheckmarkCircleFilled,
    CheckmarkCircleRegular,
    EyeFilled,
    EyeRegular,
    FolderFilled,
    FolderOpenFilled,
    FolderRegular,
    OpenFilled,
    OpenRegular,
} from '@fluentui/react-icons'
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons'
import { useTranslation } from 'react-i18next'
import FormattedDateTime from '../Date/FormattedDateTime'
import { DocumentFlatTreeItemProps, FolderDriveItem } from './DocumentFlatTree'
import { stealthClick } from '../StealthLink'
import { Destination } from '../../context/AxiosContext'
import useAxios from '../../hooks/useAxios'
import { DocumentOpenType } from '../../context/GlobalContext'
import { getDocumentExtension } from '../../helpers/documentHelper'
import useViewport, { Viewport } from '../../hooks/useViewport'

const OpenIcon = bundleIcon(OpenFilled, OpenRegular)
const PreviewIcon = bundleIcon(EyeFilled, EyeRegular)

export const useStyles = makeStyles({
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingVerticalXS,
    },
    // Explorer mode: action buttons scale down with the smaller row text (base300 -> base200),
    // so their icons go 20px -> 16px. Fluent keeps button icons at 20px even for small
    // buttons, hence the slot override.
    actionsCompact: {
        '& .fui-Button__icon': {
            fontSize: '16px',
            height: '16px',
            width: '16px',
        },
    },
    iconSizeSmall: {
        height: '16px',
        width: '16px',
        fontSize: '16px',
    },
    // Explorer mode (HAM-3993): compact rows, row-click selection. The main slot must be
    // allowed to shrink (and grow) so long file names ellipsize instead of pushing the
    // date column out of the row.
    explorerRow: {
        paddingRight: tokens.spacingHorizontalS,
        '& .fui-TreeItemLayout__main': {
            minWidth: 0,
            flexGrow: 1,
        },

        ':hover': {
            backgroundColor: tokens.colorBrandBackground2,
        },
    },
    explorerRowNarrow: {
        fontSize: tokens.fontSizeBase200,
    },
    // Fluent indents leaf rows so leaf text lines up with branch text after the expand
    // chevron. Our file rows carry their own 16px icon in iconBefore, which then lands
    // 12px right of the folder icon (that one sits centered in the 24px expandIcon slot
    // of branch rows). Mirror the expand-slot metrics on the leaf icon: pull it back into
    // that 24px box, center it, and keep the 4px gap before the text.
    explorerLeafRow: {
        '& .fui-TreeItemLayout__iconBefore': {
            marginLeft: '-8px',
            marginRight: '4px',
            paddingRight: '0',
            width: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
    },
    selectedRow: {
        backgroundColor: tokens.colorBrandBackground2Hover,
        borderLeft: '2px solid var(--colorBrandBackground2Pressed)',
    },
    mainContent: {
        minWidth: 0,
        display: 'block',
    },
    fileName: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    // Type/date shown below the selected row's name when there is no room for columns.
    metaStrip: {
        display: 'block',
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
    },
    // With a source-folder path in the strip (list view) the line must truncate instead of
    // wrapping — the full path stays reachable via the name tooltip.
    metaStripEllipsis: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    dateColumn: {
        flexShrink: 0,
        textAlign: 'right',
    },
    folderyellow: { color: tokens.colorPaletteYellowForeground1 },
    foldergrey: { color: tokens.colorPaletteAnchorBackground2 },
    folderdarkred: { color: tokens.colorPaletteRedForeground1 },
    folderlightred: { color: tokens.colorPaletteDarkOrangeBorder1 },
    folderdarkorange: { color: tokens.colorPalettePeachBorderActive },
    folderlightorange: { color: tokens.colorStatusWarningForegroundInverted },
    folderdarkgreen: { color: tokens.colorPaletteGreenForeground3 },
    folderlightgreen: { color: tokens.colorPaletteLightGreenForeground3 },
    folderdarkteal: { color: tokens.colorPaletteTealBorderActive },
    folderlightteal: { color: tokens.colorPaletteLightTealBackground2 },
    folderdarkblue: { color: tokens.colorPaletteBlueBorderActive },
    folderlightblue: { color: tokens.colorPaletteBlueBackground2 },
    folderdarkpurple: { color: tokens.colorPalettePurpleBorderActive },
    folderlightpurple: { color: tokens.colorPalettePurpleBackground2 },
    folderdarkpink: { color: tokens.colorPalettePinkBorderActive },
    folderlightpink: { color: tokens.colorPalettePinkBackground2 },
})

type SimpleDocumentFlatTreeItemProps = DocumentFlatTreeItemProps & {
    onPreviewClick?: (driveItem: FolderDriveItem) => void
    // Folder path the file lives in ("Correspondentie / 2024"), shown as a secondary line in
    // the flat list view (HAM-4057) so same-named files from different folders stay apart.
    sourceFolderPath?: string
}

function TreeItemName(props: SimpleDocumentFlatTreeItemProps & { viewportNarrow: boolean }): ReactElement {
    const styles = useStyles()
    const isFile = useMemo(() => props.itemType === 'leaf', [props.item])

    if (props.viewportNarrow || props.sourceFolderPath) {
        const tooltipContent = props.sourceFolderPath ? `${props.sourceFolderPath} / ${props.item.displayName}` : props.item.displayName
        return (
            <Tooltip relationship={'description'} withArrow content={tooltipContent} positioning={'above-start'}>
                <span className={isFile ? styles.fileName : undefined}>{props.item.displayName}</span>
            </Tooltip>
        )
    }

    return <span className={isFile ? styles.fileName : undefined}>{props.item.displayName}</span>
}

export default function SimpleDocumentFlatTreeItem(props: SimpleDocumentFlatTreeItemProps): ReactElement {
    const axios = useAxios()
    const focusTargetAttribute = useRestoreFocusTarget()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    // DocumentFlatTree's custom props travel along via the {...props} spreads of the
    // treeItemContent render helpers; strip everything that is not a FlatTreeItem prop so it
    // never reaches the DOM element.
    const {
        item,
        flatTreeHook,
        isContentLoading,
        fileCount,
        onPreviewClick,
        selectedOptions,
        allowFolderSelection,
        onMoveItems,
        sourceFolderPath,
        ...treeItemProps
    } = props

    const itemExtension = useMemo(() => getDocumentExtension(item.name), [item])

    const isFolder = useMemo(() => props.itemType === 'branch', [item])
    const isFile = useMemo(() => props.itemType === 'leaf', [item])
    const isOpen = useMemo(() => {
        return [...flatTreeHook.getTreeProps().openItems].some(id => id === props.value)
    }, [item, flatTreeHook.getTreeProps().openItems])

    const isSelected = selectedOptions?.includes(item.id) ?? false

    // Standard viewport size (see documentation/frontend/DESIGN.md).
    const viewportNarrow = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const folderColor = useMemo(() => {
        if (isFolder) {
            const color: string = item.folder?.['decorator']?.['iconColor']
            return styles[`folder${color?.toLowerCase()}`]
        }
        return undefined
    }, [])

    const itemIcon = useMemo(() => {
        if (isFolder) {
            if (isOpen) {
                return <FolderOpenFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else if (folderColor) {
                return <FolderFilled className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            } else {
                return <FolderRegular className={mergeClasses(styles.iconSizeSmall, folderColor)} />
            }
        } else {
            return (
                <img
                    alt={`${itemExtension}`}
                    className={styles.iconSizeSmall}
                    src={getFileTypeIconAsUrl({ extension: itemExtension, type: 2, imageFileType: 'svg' })}
                />
            )
        }
    }, [folderColor, isFolder, isOpen])

    const showFilePreview = isFile && onPreviewClick !== undefined
    // Every row shows a select-toggle hover action: folders need it (a row click toggles
    // open/close there) and files get it too for uniformity. Its click is handled by the
    // tree's delegated click handler via data-action.
    const showFolderSelect = isFolder && allowFolderSelection !== false
    const showActions = showFilePreview || showFolderSelect
    // From viewportNarrow down the action buttons compact along with the row text; wider
    // viewports keep the default button size.
    const compactActions = viewportNarrow
    const actionButtonSize = compactActions ? 'small' : 'medium'
    const actionsClassName = mergeClasses(styles.actions, compactActions && styles.actionsCompact)

    const aside = useMemo(() => {
        if (!isFile || viewportNarrow) {
            // At narrow viewports the date moves to the meta strip under the name.
            return undefined
        }
        return (
            <span className={styles.dateColumn}>
                <FormattedDateTime date={item.lastModifiedDateTime} />
            </span>
        )
    }, [isFile, viewportNarrow, item])

    return (
        <FlatTreeItem
            {...treeItemProps}
            {...focusTargetAttribute}
            data-node-id={item.id}
            data-node-type={isFolder ? 'folder' : 'file'}
            draggable={onMoveItems ? true : undefined}
            aria-selected={isSelected}
        >
            <TreeItemLayout
                className={mergeClasses(
                    styles.explorerRow,
                    isFile && styles.explorerLeafRow,
                    viewportNarrow && styles.explorerRowNarrow,
                    isSelected && styles.selectedRow,
                )}
                expandIcon={isFolder ? itemIcon : undefined}
                iconBefore={!isFolder && itemIcon}
                iconAfter={
                    isFolder &&
                    (isContentLoading ? (
                        <Spinner size={'extra-tiny'} />
                    ) : (
                        <Badge size={'small'} appearance={'tint'}>
                            {fileCount ?? '?'}
                        </Badge>
                    ))
                }
                aside={aside}
                actions={
                    showActions ? (
                        <>
                            {isFile && (
                                <div className={actionsClassName}>
                                    {showFilePreview && (
                                        <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.mutate.preview.button')}>
                                            <Button
                                                aria-label="Preview"
                                                appearance="subtle"
                                                size={actionButtonSize}
                                                icon={<PreviewIcon />}
                                                onClick={() => onPreviewClick?.(item)}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip relationship={'label'} withArrow content={translate('action.open')}>
                                        <Button
                                            aria-label="Preview"
                                            appearance="subtle"
                                            size={actionButtonSize}
                                            icon={<OpenIcon />}
                                            onClick={() => {
                                                stealthClick(
                                                    {
                                                        id: item.id,
                                                        destination: Destination.Documents,
                                                        documentsOpen: DocumentOpenType.Open,
                                                    },
                                                    axios,
                                                )
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                            <Tooltip relationship={'label'} withArrow content={translate('file.pivot.document.selection.toggle')}>
                                <Button
                                    aria-label={translate('file.pivot.document.selection.toggle')}
                                    aria-pressed={isSelected}
                                    appearance="subtle"
                                    size={actionButtonSize}
                                    icon={isSelected ? <CheckmarkCircleFilled /> : <CheckmarkCircleRegular />}
                                    data-action="select-toggle"
                                    data-node-id={item.id}
                                />
                            </Tooltip>
                        </>
                    ) : undefined
                }
            >
                <span className={styles.mainContent}>
                    <TreeItemName {...props} viewportNarrow={viewportNarrow} />
                    {isFile && (viewportNarrow || sourceFolderPath) && (
                        <span className={mergeClasses(styles.metaStrip, sourceFolderPath && styles.metaStripEllipsis)}>
                            {viewportNarrow && <FormattedDateTime date={item.lastModifiedDateTime} />}
                            {viewportNarrow && sourceFolderPath ? ' — ' : ''}
                            {sourceFolderPath}
                        </span>
                    )}
                </span>
            </TreeItemLayout>
        </FlatTreeItem>
    )
}
